/** AdVerify Live Detections — Blueprint Part 6.
 *
 *  Real-time monitoring board that consumes the existing SSE channel (now
 *  emitting `ad_detection` events alongside `airplay`). Three lanes:
 *    • RED   Needs attention (MISSED + EXCEPTION) — Part 4 fills this
 *    • LIVE  PENDING → CONFIRMED → CERTIFIED ticker, animating as it promotes
 *    • GREEN Today's CERTIFIED airings with one-click cert download
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { bus, BUS } from "@/lib/eventBus";
import { Button, Pill, ConfidenceBadge, EmptyState, Skeleton } from "@/design";
import { color, radius, type, font, transition } from "@/design/tokens";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_TINT = {
  DETECTED:  { bg: color.amber500 + "22", fg: color.amber500, lbl: "DETECTED" },
  PENDING:   { bg: color.amber500 + "22", fg: color.amber500, lbl: "PENDING" },
  CONFIRMED: { bg: color.cyan600 + "22",  fg: color.cyan600,  lbl: "CONFIRMED" },
  CERTIFIED: { bg: color.green500 + "22", fg: color.green500, lbl: "CERTIFIED" },
  MISSED:    { bg: color.red500 + "22",   fg: color.red500,   lbl: "MISSED" },
};

function StatusPill({ status, "data-testid": tid }) {
  const t = STATUS_TINT[status] || STATUS_TINT.PENDING;
  return (
    <span data-testid={tid} style={{
      background: t.bg, color: t.fg, fontSize: 10, fontWeight: 800,
      padding: "2px 8px", borderRadius: 999, letterSpacing: "0.06em", fontFamily: font.sans,
    }}>{t.lbl}</span>
  );
}

function DetectionRow({ d, onConfirm, confirming }) {
  const tint = STATUS_TINT[d.status] || STATUS_TINT.PENDING;
  const time = new Date(d.detected_at).toLocaleTimeString("en-GH", { hour: "2-digit", minute: "2-digit" });
  return (
    <div data-testid={`detection-${d.id}`} style={{
      display: "grid",
      gridTemplateColumns: "70px 1fr 1fr 90px 90px auto",
      gap: 14, alignItems: "center",
      padding: "10px 14px", borderRadius: radius.sm,
      background: color.navy800, border: `1px solid ${color.line}`,
      borderLeft: `3px solid ${tint.fg}`,
      animation: "ws-fade-in 220ms ease-out",
    }}>
      <div style={{ ...type.number, color: color.slate400, fontSize: 12 }}>{time}</div>
      <div>
        <div style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>{d.station_name}</div>
        <div style={{ color: color.slate400, fontSize: 11 }}>{d.brand}</div>
      </div>
      <div style={{ color: "#fff", fontSize: 13 }}>{d.ad_title}</div>
      <div style={{ ...type.number, fontSize: 13, color: color.green500 }} title="Audience at air">
        {(d.audience_at_detection || 0).toLocaleString()}
      </div>
      <StatusPill status={d.status} data-testid={`detection-${d.id}-status`} />
      <div style={{ display: "flex", gap: 6 }}>
        <ConfidenceBadge source={d.confidence_label || "MODEL"} size="xs" tone="dark" />
        {(d.status === "DETECTED" || d.status === "PENDING") && (
          <Button data-testid={`detection-${d.id}-confirm`}
            size="sm" variant="secondary"
            audience="station"
            loading={confirming === d.id}
            onClick={() => onConfirm(d)}>
            Confirm
          </Button>
        )}
        {d.status === "CERTIFIED" && d.certificate_number && (
          <Button data-testid={`detection-${d.id}-download`}
            size="sm" variant="ghost"
            onClick={() => window.open(`${BACKEND_URL}/api/certificates/${d.certificate_number}/pdf`, "_blank")}>
            Download
          </Button>
        )}
      </div>
    </div>
  );
}

export function AdVerifyLive({ "data-testid": tid }) {
  const [detections, setDetections] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const sseRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/adverify/detections?limit=100");
      setDetections(data.detections || []);
      setCounts(data.counts || {});
    } finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    // Subscribe to the existing SSE channel — emits both legacy "airplay" and
    // new "ad_detection" events. We rerender by prepending the new row.
    try {
      const url = `${BACKEND_URL}/api/realtime/airplay/stream`;
      sseRef.current = new EventSource(url, { withCredentials: false });
      sseRef.current.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type !== "ad_detection") return;
          setDetections((prev) => {
            // upsert by id — promotions reuse the same id and replace in place
            const idx = prev.findIndex((d) => d.id === ev.id);
            if (idx >= 0) {
              const copy = [...prev];
              copy[idx] = { ...prev[idx], ...ev };
              return copy;
            }
            return [ev, ...prev].slice(0, 100);
          });
          setCounts((c) => ({ ...c, [ev.status]: (c[ev.status] || 0) + 1 }));
          // Fan out to siblings (e.g. AdVerifyBookings) — they care about
          // MISSED rows because a MISSED detection flips a booking's status.
          bus.emit(BUS.AD_DETECTION, ev);
        } catch (e) { /* ignore */ }
      };
    } catch (e) { /* SSE optional */ }
    return () => { try { sseRef.current?.close(); } catch (e) { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async (d) => {
    setConfirming(d.id);
    const tid = toast.loading(`Confirming ${d.station_name} · ${d.ad_title}…`);
    try {
      const { data } = await api.post(`/adverify/detections/${d.id}/confirm`);
      if (data.status === "CONFIRMED" || !data.certificate_number) {
        toast.success("Confirmed", {
          id: tid,
          description: data.message || "Metadata-confirmed · fingerprint verification pending.",
        });
      } else {
        toast.success("Certified ✅", { id: tid, description: `Certificate #${data.certificate_number} issued.` });
      }
      refresh();
    } catch (e) {
      toast.error("Could not confirm", { id: tid, description: e.response?.data?.detail || e.message });
    } finally { setConfirming(null); }
  };

  const missed = useMemo(() => detections.filter((d) => d.status === "MISSED"), [detections]);
  const live   = useMemo(() => detections.filter((d) => d.status === "DETECTED" || d.status === "PENDING" || d.status === "CONFIRMED"), [detections]);
  const certified = useMemo(() => detections.filter((d) => d.status === "CERTIFIED"), [detections]);

  return (
    <div data-testid={tid || "adverify-live"} style={{ display: "flex", flexDirection: "column", gap: 18, fontFamily: font.sans }}>
      {/* Live counter strip */}
      <div data-testid="adverify-counters" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {[
          { label: "Confirmed today", val: counts.CERTIFIED || 0, color: color.green500 },
          { label: "Pending",         val: (counts.PENDING || 0) + (counts.DETECTED || 0), color: color.amber500 },
          { label: "Exceptions",      val: counts.MISSED || 0, color: color.red500 },
        ].map((c) => (
          <div key={c.label} style={{ flex: "1 1 160px", background: color.navy800, border: `1px solid ${color.line}`, borderRadius: radius.lg, padding: "14px 18px" }}>
            <div style={{ ...type.micro, color: color.slate400 }}>{c.label}</div>
            <div style={{ ...type.number, fontSize: 28, color: c.color, marginTop: 4 }}>{c.val}</div>
          </div>
        ))}
      </div>

      {/* Fingerprint-pending banner — a metadata match is a *sighting*, not
          audio-verified proof. Until ACRCloud is live, the ladder stops at
          CONFIRMED and no CERTIFIED cert is minted. */}
      <div data-testid="adverify-dormant-note" style={{
        background: color.amber500 + "11", border: `1px solid ${color.amber500}55`,
        color: color.amber500, fontSize: 12, padding: "10px 14px", borderRadius: radius.md,
        display: "flex", gap: 10, alignItems: "center",
      }}>
        <Pill variant="warning">METADATA</Pill>
        <span style={{ color: color.slate400 }}>
          Fingerprint verification coming — detections are metadata-confirmed until then. No certificate is issued from metadata alone.
        </span>
      </div>

      {/* Red lane — exceptions */}
      <section>
        <h3 style={{ ...type.h3, color: color.red500, margin: "0 0 8px" }}>🚨 Needs attention ({missed.length})</h3>
        {missed.length === 0 ? (
          <div style={{ color: color.slate400, fontSize: 12, padding: "10px 14px", background: color.navy800, border: `1px dashed ${color.line}`, borderRadius: radius.md }}>
            No missed spots — every booking aired in its window.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {missed.map((d) => <DetectionRow key={d.id} d={d} onConfirm={confirm} confirming={confirming} />)}
          </div>
        )}
      </section>

      {/* Amber/cyan lane — live ticker */}
      <section>
        <h3 style={{ ...type.h3, color: color.cyan600, margin: "0 0 8px" }}>📡 Live ticker ({live.length})</h3>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton variant="line" tone="dark" height={42} />
            <Skeleton variant="line" tone="dark" height={42} />
          </div>
        ) : live.length === 0 ? (
          <EmptyState tone="dark"
            title="Quiet right now."
            helper="When one of your ads is detected on a monitored station, it'll appear here in real time."
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {live.map((d) => <DetectionRow key={d.id} d={d} onConfirm={confirm} confirming={confirming} />)}
          </div>
        )}
      </section>

      {/* Green lane — certified */}
      <section>
        <h3 style={{ ...type.h3, color: color.green500, margin: "0 0 8px" }}>✅ Certified airings ({certified.length})</h3>
        {certified.length === 0 ? (
          <div style={{ color: color.slate400, fontSize: 12, padding: "10px 14px", background: color.navy800, border: `1px dashed ${color.line}`, borderRadius: radius.md }}>
            No certified airings yet today. They'll show up here with one-click PDF download.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {certified.map((d) => <DetectionRow key={d.id} d={d} onConfirm={confirm} confirming={confirming} />)}
          </div>
        )}
      </section>
    </div>
  );
}
